import { type ReactNode } from 'react';
import { type SocialNetworkName } from 'dibs-social-networks/exports/types';
import { type SingleOnChangeArgs } from 'dibs-elements/exports/SingleSelect';
import { type MultiOnChangeArgs } from 'dibs-elements/exports/MultiSelect';

import {
    validateRequiredField,
    validateNoSpecialCharacters,
    validateEmail,
} from '../../common/helpers/formValidationHelpers';

import {
    noSpecialCharactersAddress,
    zipCodeIsInvalid,
    isEstimatedInventorySizeOverLimit,
    arrayIsNotEmpty,
    validateEori,
} from './sellerApplicationValidationHelpers';

import {
    type FormDataContactDetails,
    type FormDataBusinessDetails,
    type BusinessItem,
    type FormDataAdditionalDetails,
} from '../form/SellerApplicationFormReducer';

type SharedFields =
    | keyof FormDataContactDetails
    | keyof FormDataBusinessDetails
    | keyof BusinessItem
    | keyof FormDataAdditionalDetails;

export type SocialNetworkList = SocialDetails[] | null;

type SocialDetails = {
    socialUserName?: string;
    socialUrl: string;
    network: SocialNetworkName;
};

type MaybeString = string | null | undefined;

function getSharedErrors(values: {
    [fieldName in Exclude<SharedFields, 'SocialDetails'>]?:
        | string
        | string[]
        | boolean
        | SocialNetworkList
        | SingleOnChangeArgs
        | MultiOnChangeArgs
        | File
        | File[];
}): {
    [fieldName in SharedFields]: ReactNode;
} {
    return {
        firstName:
            validateRequiredField(values.firstName) ||
            validateNoSpecialCharacters(values.firstName as MaybeString),
        lastName:
            validateRequiredField(values.lastName) ||
            validateNoSpecialCharacters(values.lastName as MaybeString),
        email: validateRequiredField(values.email) || validateEmail(values.email as MaybeString),
        phoneNumber: validateRequiredField(values.phoneNumber),
        businessName: validateRequiredField(values.businessName),
        country:
            validateRequiredField(values.country) ||
            validateNoSpecialCharacters(values.country as MaybeString),
        line1: validateRequiredField(values.line1) || noSpecialCharactersAddress(values.line1),
        city:
            validateRequiredField(values.city) ||
            validateNoSpecialCharacters(values.city as MaybeString),
        state:
            validateRequiredField(values.state) ||
            validateNoSpecialCharacters(values.state as MaybeString),
        zipCode:
            validateRequiredField(values.zipCode) ||
            zipCodeIsInvalid({ country: values.country, zipCode: values.zipCode }),
        eori: validateRequiredField(values.eori) || validateEori(values.eori),
        experienceSellingOnline: validateRequiredField(values.experienceSellingOnline),
        primaryBusinessFocus: validateRequiredField(values.primaryBusinessFocus),
        businessType: validateRequiredField(values.businessType),
        galleryRepresentation: validateRequiredField(values.galleryRepresentation),
        whereRepresented: validateRequiredField(values.whereRepresented),
        specialty: validateRequiredField(values.specialty),
        title: validateRequiredField(values.title),
        category: validateRequiredField(values.category),
        subCategory: validateRequiredField(values.subCategory),
        description: validateRequiredField(values.description),
        creatorArtistDesigner: validateRequiredField(values.creatorArtistDesigner),
        imageFiles: validateRequiredField(values.imageFiles) || arrayIsNotEmpty(values.imageFiles),
        price: validateRequiredField(values.price),
        period: validateRequiredField(values.period),
        condition: validateRequiredField(values.condition),
        estimatedInventorySize:
            validateRequiredField(values.estimatedInventorySize) ||
            isEstimatedInventorySizeOverLimit(values.estimatedInventorySize),
        averageItemPriceCurrency: validateRequiredField(values.averageItemPriceCurrency),
        averageItemPriceValue: validateRequiredField(values.averageItemPriceValue),
        numSoldLastYear: validateRequiredField(values.numSoldLastYear),
        estimatedMonthlyUploads: validateRequiredField(values.estimatedMonthlyUploads),
        isSellerOnOtherMarketPlace: validateRequiredField(values.isSellerOnOtherMarketPlace),
        otherMarketPlaceDetails: validateRequiredField(values.otherMarketPlaceDetails),
        hoursManagePerWeek: validateRequiredField(values.hoursManagePerWeek),
        hasWebsiteOrSocialMedia: validateRequiredField(values.hasWebsiteOrSocialMedia),
        socialNetworks: validateRequiredField(values.socialNetworks),
        preferredLanguage: validateRequiredField(values.preferredLanguage),
        associations: validateRequiredField(values.associations),
        fairs: validateRequiredField(values.fairs),
        locations: validateRequiredField(values.locations),
        locationsWithinSelectedCountry: validateRequiredField(
            values.locationsWithinSelectedCountry
        ),
        ownInventory: validateRequiredField(values.ownInventory),
        consignmentBasis: validateRequiredField(values.consignmentBasis),
    };
}

type SellerApplicationFields = SharedFields | 'averageItemPriceValue' | 'socialNetworks';

type SellerApplicationFormValidatorValues = {
    [fieldName in SellerApplicationFields]?:
        | string
        | string[]
        | boolean
        | SocialNetworkList
        | SingleOnChangeArgs
        | MultiOnChangeArgs
        | File
        | File[];
};

export function sellerApplicationFormFieldsValidation(
    values: SellerApplicationFormValidatorValues
): {
    isValid: boolean;
    errors: { [fieldName in SellerApplicationFields]: ReactNode };
} {
    const fieldsToValidate = Object.keys(values || {});

    const sellerApplicationErrors = {
        ...getSharedErrors(values),
        averageItemPriceValue: validateRequiredField(values.averageItemPriceValue),
        socialNetworks: arrayIsNotEmpty(values.socialNetworks),
    };

    const validatedErrorFields = fieldsToValidate.reduce(
        (acc, cur) => ({
            ...acc,
            [cur]: sellerApplicationErrors[cur as SellerApplicationFields],
        }),
        {}
    ) as {
        [fieldName in SellerApplicationFields]: ReactNode;
    };

    return {
        errors: validatedErrorFields,
        isValid: Object.keys(values).every(
            key => !sellerApplicationErrors[key as SellerApplicationFields]
        ),
    };
}
